import { fetchApiTracks } from "@src/apis/trackApis";
import { useAuthCurator, useRole } from "@src/hooks/useAuth";
import { QUERY_KEYS } from "@src/queries/queryKeys";
import { ApiResponse } from "@src/types/httpApi";
import { ITrack } from "@src/types/track";
import { useInfiniteQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import styled from "styled-components";
import TableComponent from "../TableComponent";
import { existingTracksColumns } from "./columns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Spinner from "../Spinner";
import { useNotification } from "@src/context/NotificationContext";
import { useAddTrackToPlaylistMutation } from "@src/queries/trackQueries";
import { useAppSelector } from "@src/redux/hooks";
import { useDebounce } from "@src/hooks/useDebounce";
import { UIButton } from "../UIButton";
import UIModal from "../UIModal/UIModal";
import useModal from "@src/hooks/useModal";
import { theme } from "@src/theme";

const ExistingTracksList = ({
  addTrackCallback,
  playlistId,
  handlePlayButtonClick,
}: {
  addTrackCallback: () => void;
  playlistId: string;
  handlePlayButtonClick: (track: ITrack) => void;
}) => {
  const { documentId: curatorId } = useAuthCurator();
  const role = useRole();
  const { addNotification } = useNotification();
  const {
    searchTracksText,
    tracksTagFilter: { tags: tracksTagFilter, excludedTags: excludedTags },
    moodsFilter: { moods: moodsFilter, excludedMoods: excludedMoods },
    trackLibraryFilters,
  } = useAppSelector((state) => state.moodFactory);
  const { currentPlayingTrack, trackStatus } = useAppSelector(
    (state) => state.trackPlayer
  );
  const debouncedTracksTagFilter = useDebounce(tracksTagFilter, 900);
  const debouncedExcludedTags = useDebounce(excludedTags, 900);
  const debouncedMoodsFilter = useDebounce(moodsFilter, 900);
  const debouncedExcludedMoods = useDebounce(excludedMoods, 900);
  const debouncedLibraryFilters = useDebounce(trackLibraryFilters, 900);
  const connectTrackMutation = useAddTrackToPlaylistMutation();
  //   Infinite fetching tracks list - Start
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
  } = useInfiniteQuery({
    queryKey: [
      QUERY_KEYS.INFINITE_TRACKS,
      {
        searchText: searchTracksText,
        excludedTags:
          debouncedExcludedTags?.map((tag) => ({
            documentId: tag.documentId,
          })) ?? [],
        tags:
          debouncedTracksTagFilter?.map((tag) => ({
            documentId: tag.documentId,
          })) ?? [],
        moods:
          debouncedMoodsFilter?.map((mood) => ({
            documentId: mood.documentId,
          })) ?? [],
        debouncedExcludedMoods:
          debouncedExcludedMoods?.map((mood) => ({
            documentId: mood.documentId,
          })) ?? [],
        libraryFilters: debouncedLibraryFilters,
      },
      null,
    ],
    queryFn: ({ pageParam = 1 }: { pageParam?: number }) =>
      fetchApiTracks({
        filters: {
          ...debouncedLibraryFilters,
          searchText: searchTracksText,
          includedTags:
            debouncedTracksTagFilter?.map((tag) => ({
              documentId: tag.documentId,
            })) || [],
          excludedTags:
            debouncedExcludedTags?.map((tag) => ({
              documentId: tag.documentId,
            })) || [],
          includedMoods:
            debouncedMoodsFilter?.map((mood) => ({
              documentId: mood.documentId,
            })) || [],
          excludedMoods:
            debouncedExcludedMoods?.map((mood) => ({
              documentId: mood.documentId,
            })) || [],
        },
        page: pageParam,
        pageSize: 50,
      }),
    getNextPageParam: (lastPage: AxiosResponse<ApiResponse<ITrack[]>>) => {
      if (!lastPage?.data?.meta?.pagination) {
        return undefined;
      }
      const { page: currentPage, pageCount: totalPages } =
        lastPage.data.meta.pagination;
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    enabled: !!curatorId || role === "admin" || role === "manager",
    initialPageParam: 1,
  });
  //   Infinite fetching tracks list - End

  // Infinite scrolling tracks list - Start
  const tracks = useMemo(
    () =>
      ((data?.pages as AxiosResponse<ApiResponse<ITrack[]>>[]) ?? []).flatMap(
        (response) => response.data.data
      ) || [],
    [data]
  );

  const observerRef = useRef<HTMLTableRowElement | null>(null);
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];
      if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, hasNextPage]
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      threshold: 0.1,
    });

    if (observerRef.current) observer.observe(observerRef.current);

    return () => {
      if (observerRef.current) observer.unobserve(observerRef.current);
    };
  }, [handleObserver]);
  // Infinite scrolling tracks list - End

  const addTrackToPlaylist = useCallback(
    (track: ITrack) => {
      if (role !== "admin" && role !== "manager") {
        addNotification(
          "You can only add tracks that you have created to a playlist.",
          "error"
        );
        return;
      }
      if (!playlistId) {
        addNotification("No playlist selected to add the track.", "error");
        return;
      }
      if (!track.documentId) {
        addNotification("Track does not have a valid document ID.", "error");
        return;
      }

      const isAlreadyInPlaylist = track.foundInPlaylist?.some(
        (fip) => String(fip.id) === playlistId || fip.documentId === playlistId
      );

      if (isAlreadyInPlaylist) {
        addNotification("Track already in playlist", "error");
        return;
      }

      connectTrackMutation.mutateAsync(
        {
          playlistId,
          trackId: track.documentId,
        },
        {
          onSuccess: () => {
            addTrackCallback();
            addNotification(
              `Track "${track.title}" added to playlist successfully.`,
              "success"
            );
          },
          onError: (error) => {
            addNotification(
              `Failed to add track "${track.title}" to playlist: ${error.message}`,
              "error"
            );
          },
        }
      );
    },
    [playlistId]
  );
  const { isOpen, openModal, closeModal } = useModal();
  const [processing, setProcessing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [tracksProcessedCount, setTracksProcessedCount] = useState(0);
  const isCancelledRef = useRef(false);

  // Reset UI state when modal closes (but NOT the cancellation flag while processing)
  useEffect(() => {
    if (!isOpen && !processing) {
      // Only reset when modal closes AND processing is complete
      setTracksProcessedCount(0);
      setCancelling(false);
    }
  }, [isOpen, processing]);

  const onCancel = (): void => {
    if (processing) {
      isCancelledRef.current = true;
      setCancelling(true);
      // Don't close modal immediately - let the process handle it
    } else {
      closeModal();
    }
  };

  const onBulkAddTracksConfirm = async (): Promise<void> => {
    if (processing) return;
    setProcessing(true);
    setCancelling(false);
    isCancelledRef.current = false;
    setTracksProcessedCount(0);

    const tracksDocumentIds = tracks
      .filter((track) => !!track.documentId)
      .filter((track) => {
        const isAlreadyInPlaylist = track.foundInPlaylist?.some(
          (fip) =>
            String(fip.id) === playlistId || fip.documentId === playlistId
        );
        return !isAlreadyInPlaylist;
      })
      .map((track) => track.documentId!) as string[];

    // Limit to 300 tracks per selection
    const MAX_TRACKS_LIMIT = 300;
    const limitedTracksDocumentIds = tracksDocumentIds.slice(
      0,
      MAX_TRACKS_LIMIT
    );

    if (limitedTracksDocumentIds.length === 0) {
      addNotification(
        "All tracks in this view are already in the playlist.",
        "info"
      );
      setProcessing(false);
      return;
    }

    if (role !== "admin" && role !== "manager") {
      addNotification(
        "You can only add tracks that you have created to a playlist.",
        "error"
      );
      setProcessing(false);
      return;
    }
    if (!playlistId) {
      addNotification("No playlist selected to add the tracks.", "error");
      setProcessing(false);
      return;
    }
    if (limitedTracksDocumentIds.length === 0) {
      addNotification("No valid tracks to add to the playlist.", "error");
      setProcessing(false);
      return;
    }

    let successCount = 0;
    for (const trackId of limitedTracksDocumentIds) {
      // Check cancellation using ref
      if (isCancelledRef.current) {
        break;
      }

      try {
        await connectTrackMutation.mutateAsync(
          {
            playlistId,
            trackId,
          },
          {
            onSuccess: () => {
              setTracksProcessedCount((prev) => prev + 1);
            },
            onError: (error) => {
              addNotification(
                `Failed to add track with ID "${trackId}" to playlist: ${error.message}`,
                "error"
              );
            },
          }
        );
        successCount++;
      } catch (error: any) {
        addNotification(
          `Failed to add track with ID "${trackId}" to playlist: ${error.message}`,
          "error"
        );
      }
    }

    if (successCount > 0 && !isCancelledRef.current) {
      addNotification(
        `Added ${successCount} tracks to playlist successfully.`,
        "success"
      );
    }

    if (isCancelledRef.current) {
      addNotification(
        `Process cancelled. ${successCount} tracks were added before cancellation.`,
        "info"
      );
    }

    addTrackCallback();

    // Reset states
    setProcessing(false);
    setCancelling(false);
    isCancelledRef.current = false;

    // Close modal after a brief delay to show final count
    setTimeout(() => {
      closeModal();
    }, 800);
  };
  const onConfirm = (): void => {
    onBulkAddTracksConfirm();
  };

  const MAX_TRACKS_LIMIT = 300;
  const tracksToAdd = Math.min(tracks?.length || 0, MAX_TRACKS_LIMIT);

  return (
    <TableContentWrapper>
      <ButtonsContainer>
        <UIButton
          disabled={processing || tracks?.length === 0 || isFetching}
          style={{ width: 120 }}
          onClick={openModal}
        >
          Add {tracksToAdd}{" "}
          {tracks?.length > MAX_TRACKS_LIMIT && `of ${tracks?.length}`} Tracks
        </UIButton>
      </ButtonsContainer>
      <UIModal
        actions={[
          <UIButton
            style={{ width: processing ? 120 : "auto" }}
            disabled={isFetching || cancelling}
            key="cancel"
            onClick={onCancel}
            variant="outline"
          >
            {cancelling
              ? "Cancelling..."
              : processing
                ? "Cancel Process"
                : "Cancel"}
          </UIButton>,
          <UIButton
            disabled={processing || isFetching || cancelling}
            key="confirm"
            onClick={onConfirm}
            variant="primary"
          >
            Confirm
          </UIButton>,
        ]}
        isOpen={isOpen}
        onClose={onCancel}
        title="Confirm Bulk Track Addition"
      >
        <p>
          Are you sure you want to add <strong>{tracksToAdd}</strong> track
          {tracksToAdd !== 1 ? "s" : ""}?
          {tracks.length > MAX_TRACKS_LIMIT && (
            <>
              <br />
              <small style={{ color: theme.colors.gray }}>
                Note: Only the first {MAX_TRACKS_LIMIT} tracks will be added
                from {tracks.length} total tracks.
              </small>
            </>
          )}
        </p>
        {processing && (
          <p style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Spinner small /> {cancelling && "Cancelling..."}
          </p>
        )}
        <p>
          <strong>{tracksProcessedCount}</strong> of{" "}
          <strong>{tracksToAdd}</strong> tracks processed.
        </p>
      </UIModal>
      <TableComponent
        variant="grayBg"
        $compactUI
        columns={existingTracksColumns({
          onPlayTrack: handlePlayButtonClick,
          onAddTrackToPlaylist: addTrackToPlaylist,
          currentTrack: currentPlayingTrack,
          trackStatus,
        })}
        data={tracks}
        loaderRef={observerRef}
        loadingNextPage={isFetchingNextPage || isLoading}
      />
    </TableContentWrapper>
  );
};

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  padding: ${({ theme }) => theme.margins.mediumL};
  padding-top: 0;
`;

const TableContentWrapper = styled.div`
  width: 100%;
  overflow: auto;
`;

export default ExistingTracksList;
